/**
 * Verification Export Dialog
 *
 * Dialog for exporting verification session as Excel.
 *
 * DELIBERATELY NOT MIGRATED to react-hook-form + Zod, unlike its sibling
 * dialogs. This is a confirmation, not a form: it has no field of any kind —
 * no text input, no checkbox, no format or scope selection — so there is no
 * user value to collect and nothing to validate. The export is a
 * `GET /verification/sessions/{session_id}/export/excel`
 * (api/routes/verification.py:460) with a single path parameter and no request
 * body, and the filename is derived from `session`, which arrives as a prop and
 * is already guarded by the `if (!session) return null` below. An RHF wrapper
 * here would be an empty schema and a handleSubmit around handleExport: more
 * indirection, zero validation.
 *
 * Its only gate is in-flight disabling, pinned in the test beside this file.
 * Revisit if the dialog ever gains real options (format, unit subset, date
 * range) — that is the point at which it becomes a form.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Download } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LoadingButton } from '@/components/common/LoadingButton';
import { VerificationSession } from '../../types/verification';
import { downloadExportedExcel } from '../../services/verificationService';
import { resolveApiErrorMessage } from '../../utils/apiErrorMessages';

interface VerificationExportDialogProps {
  open: boolean;
  session: VerificationSession | null;
  onClose: () => void;
}

const VerificationExportDialog: React.FC<VerificationExportDialogProps> = ({
  open,
  session,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    if (!session) return;

    setIsExporting(true);
    setError(null);

    try {
      const filename = `設計検証項目_${session.title}_${session.id.slice(0, 8)}.xlsx`;
      await downloadExportedExcel(session.id, filename);
      onClose();
    } catch (err) {
      setError(resolveApiErrorMessage(t, err, { fallbackKey: 'verification.export.error' }));
    } finally {
      setIsExporting(false);
    }
  };

  if (!session) return null;

  const totalItems = session.units.reduce((sum, u) => sum + u.items.length, 0);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* The inset keeps the MUI dialog's viewport gutter, which `w-full` alone drops. */}
      <DialogContent
        className="revy-ui w-[calc(100%-2rem)] max-w-[600px] sm:max-w-[600px]"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="revy-cjk text-lg leading-[1.5]">
            {t('verification.export.dialogTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-1">
          <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
            {t('verification.export.sessionLabel')}: {session.title}
          </p>
          {session.modelName && (
            <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
              {t('verification.modelName')}: {session.modelName}
            </p>
          )}
          <p className="revy-cjk revy-figure text-sm leading-[1.5] text-muted-foreground">
            {session.units.length} {t('verification.units')} / {totalItems} {t('verification.items')}
          </p>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <CircleAlert className="size-4" strokeWidth={2} />
              <AlertDescription className="revy-cjk text-sm leading-[1.6]">
                {error}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={isExporting}>
            <span className="revy-cjk">{t('common.cancel')}</span>
          </Button>
          <LoadingButton
            onClick={handleExport}
            loading={isExporting}
            loadingText={t('verification.export.exporting')}
          >
            <Download strokeWidth={1.9} />
            <span className="revy-cjk">{t('verification.export.download')}</span>
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationExportDialog;
